import { getRoundedPercentageOfValue, getRoundedValueOfPercentage } from '@garnish/core';
import { isToday } from 'date-fns/isToday';

import { OrderState } from '@sg/graphql-schema';

import { getMaxTipAmount } from '../utils/tipping';
import { adjustWantedTime } from '../utils/wantedTimes';
import { type OrderingContext } from './ordering-machine.context';
import {
  type OrderingCart,
  type OrderingLedger,
  type UpsellRailSnapshot,
  type WantedTime,
} from './ordering-machine.types';

export const isWantedTimeToday = (time?: WantedTime) => {
  const validTime = adjustWantedTime(time);

  return Boolean(validTime && isToday(validTime));
};

export const getNewTip = (
  previousTip: number,
  previousMaxTipAmount: number,
  availableTips: ReadonlyArray<number | 'CUSTOM'>,
  newLedger?: OrderingLedger,
): number => {
  const previousPercentage = getRoundedPercentageOfValue(previousTip, previousMaxTipAmount);

  if (availableTips.includes(previousPercentage)) {
    const newMaximumTip = getMaxTipAmount(newLedger);
    return getRoundedValueOfPercentage(previousPercentage, newMaximumTip) || 0;
  }

  const newMaximumTip = getMaxTipAmount(newLedger);
  if (previousTip > newMaximumTip) {
    return newMaximumTip;
  }

  return previousTip;
};

export function getMaxAvailableUpsellItems(params: GetMaxAvailableUpsellItemsParams) {
  const { lineItems, upsellItems } = params;

  const cartProductIds = new Set(lineItems.map((lineItem) => lineItem.product?.id));
  const productsNotInCart = upsellItems.filter((item) => !cartProductIds.has(item.id));

  return productsNotInCart.slice(0, MAX_NUMBER_UPSELLS);
}

export function getUpsellRailSnapshot(
  params: GetMaxAvailableUpsellItemsParams,
): UpsellRailSnapshot {
  const railItems = getMaxAvailableUpsellItems(params);

  return {
    railItemCount: railItems.length,
    personalizedItemCount: railItems.filter((item) => item.isPersonalized).length,
  };
}

export function checkIfCartCanShowUpsells(cart: UpsellEligibleCart): boolean {
  const { isGroupOrder, canSubmitOrder, orderState, lineItems } = cart;

  const hasLineItems = lineItems.length > 0;
  const canSubmitCartOrder = !isGroupOrder || canSubmitOrder;
  const isGroupOrderLocked = isGroupOrder && LOCKED_ORDER_STATES.has(orderState);

  return hasLineItems && canSubmitCartOrder && !isGroupOrderLocked;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_NUMBER_UPSELLS = 6;

const LOCKED_ORDER_STATES = new Set<OrderState>([
  OrderState.LockedCheckout,
  OrderState.LockedChangeLocation,
]);

// ─── Types ───────────────────────────────────────────────────────────────────

type UpsellEligibleCart = Pick<
  OrderingCart,
  'isGroupOrder' | 'canSubmitOrder' | 'orderState' | 'lineItems'
>;

type GetMaxAvailableUpsellItemsParams = {
  lineItems: OrderingContext['cart']['lineItems'];
  upsellItems: OrderingContext['upsellItems'];
};
